/**
 * CSV / PDF downloads for Progress Billing (WIP schedule, completed
 * projects, billing summary). CSV uses the generic report exporter; PDF
 * uses the same @react-pdf/renderer cover + schedule as proposal downloads.
 */
import { pdf } from '@react-pdf/renderer';
import type { ReportColumn } from '../types';
import { ProgressBillingPdfDocument } from '../components/finances/ProgressBillingPdfDocument';
import { exportToCSV } from './reportExport';
import { loadLctfPdfLogoBase64 } from './lctfPdfLogo';
import {
  buildProgressBillingReport,
  type ProgressBillingProject,
  type ProgressBillingReport,
  type ProgressBillingReportKind,
} from './progressBilling';
import { progressBillingReportFilename } from './progressBillingPdfLayout';

export {
  PROGRESS_BILLING_COMPANY_NAME,
  PROGRESS_BILLING_COMPANY_WEBSITE,
  PROGRESS_BILLING_PDF_METHOD_NOTE,
  formatProgressBillingPdfCell,
  longDate,
  progressBillingCoverSubtitle,
  progressBillingPdfColumnFlex,
  progressBillingPdfColumns,
  progressBillingPdfKpis,
  progressBillingPdfRows,
  progressBillingPdfTitle,
  progressBillingPdfTotals,
  progressBillingReportFilename,
} from './progressBillingPdfLayout';

export function toReportColumns(report: ProgressBillingReport): ReportColumn[] {
  return report.columns.map(col => ({ field: col.field, label: col.label, type: col.type, visible: true }));
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function exportProgressBillingCsv(
  kind: ProgressBillingReportKind,
  projects: ProgressBillingProject[],
  asOf: Date,
): Promise<ProgressBillingReport> {
  const report = buildProgressBillingReport(kind, projects, asOf);
  await exportToCSV({
    filename: progressBillingReportFilename(report, 'csv'),
    columns: toReportColumns(report),
    data: report.rows,
  });
  return report;
}

export async function exportProgressBillingPdf(
  kind: ProgressBillingReportKind,
  projects: ProgressBillingProject[],
  asOf: Date,
): Promise<ProgressBillingReport> {
  const report = buildProgressBillingReport(kind, projects, asOf);
  const companyLogo = await loadLctfPdfLogoBase64();
  const blob = await pdf(
    <ProgressBillingPdfDocument report={report} companyLogo={companyLogo} />,
  ).toBlob();
  downloadBlob(blob, progressBillingReportFilename(report, 'pdf'));
  return report;
}
